import React, { FC, ReactNode } from 'react';
import { formatClassName } from '../../../utils';
import { Icon } from '../Icon';
import { IconProps } from '../Icon/types';
import { Field, FieldErrorTextProps as ChakraFormErrorMessageProps, Flex } from '@chakra-ui/react';
const ChakraFormErrorMessage = Field.ErrorText;

export interface FormErrorMessageProps extends Omit<ChakraFormErrorMessageProps, 'children'> {
  /** Error content as children — ReactNode for full layout control */
  children?: ReactNode;
  /** Error content — string or any ReactNode (e.g. formatted text, links) */
  errorMessage?: ReactNode;
  /**
   * Props forwarded to the leading error icon.
   * Override to change icon name, size, color, etc.
   * Defaults to `{ iconName: 'octagon-exclamation', prefix: 'fas' }`.
   */
  iconProps?: Partial<IconProps>;
  /**
   * Set to false to suppress the leading icon entirely,
   * leaving layout control entirely to children/errorMessage.
   */
  showIcon?: boolean;
}

export const ErrorMessage: FC<FormErrorMessageProps> = ({
  children,
  className,
  errorMessage,
  iconProps,
  showIcon = true,
  ...rest
}) => {
  if (!errorMessage && !children) return null;

  const resolvedIconProps: IconProps = {
    iconName: 'octagon-exclamation',
    prefix: 'fas',
    ...iconProps,
  } as IconProps;

  return (
    <ChakraFormErrorMessage
      role="alert"
      gap={0}
      className={formatClassName(['form-error-message', className])}
      {...rest}
    >
      {showIcon && (
        <Flex marginRight="4px">
          <Icon {...resolvedIconProps} />
        </Flex>
      )}
      {children ?? errorMessage}
    </ChakraFormErrorMessage>
  );
};

ErrorMessage.displayName = 'FormErrorMessage';
